import { Sparkles, User, Bot, FileText, Layers, FolderOpen, MessageSquare, Shield } from "lucide-react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export interface PersonalizationDetails {
  hasVoiceProfile: boolean;
  voiceProfileFields: string[];
  hasAgent: boolean;
  agentName?: string;
  agentLens?: string;
  sourceCount: number;
  sourceNames: string[];
  spaceCount: number;
  spaceNames: string[];
  hasSpaceLens?: boolean;
  hasTopicContext: boolean;
  topicName?: string;
  chatMode: string;
  sourceOfTruthCount?: number;
}

interface PersonalizationIndicatorProps {
  details: PersonalizationDetails;
  className?: string;
  /** Render as a compact icon-only trigger (no text label) for tight header bars. */
  iconOnly?: boolean;
}

const FIELD_LABELS: Record<string, string> = {
  tone: "Tone",
  verbosity: "Verbosity",
  learningStyle: "Learning Style",
  responseFormat: "Response Format",
  writingStyle: "Writing Style",
  expertise: "Expertise",
  background: "Background",
  worldview: "Worldview",
  goals: "Goals",
  communicationPreferences: "Communication",
  favoritePhrases: "Favorite Phrases",
  avoidPhrases: "Avoid Phrases",
  referenceAuthors: "Reference Authors"
};

const CHAT_MODE_LABELS: Record<string, string> = {
  normal: "Standard",
  questioning: "Questioning",
  dialogue: "Dialogue"
};

export function PersonalizationIndicator({ details, className = "", iconOnly = false }: PersonalizationIndicatorProps) {
  const isPersonalized = details.hasVoiceProfile || 
    details.hasAgent || 
    details.sourceCount > 0 || 
    details.spaceCount > 0 || 
    details.hasTopicContext ||
    details.chatMode !== "normal";

  if (!isPersonalized) {
    return null;
  }

  const activeCount = [
    details.hasVoiceProfile,
    details.hasAgent,
    details.sourceCount > 0,
    details.spaceCount > 0,
    details.hasTopicContext,
    details.chatMode !== "normal",
    (details.sourceOfTruthCount ?? 0) > 0,
  ].filter(Boolean).length;

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        {iconOnly ? (
          <Button
            variant="ghost"
            size="icon"
            className={className}
            title="Active personalization"
            data-testid="badge-personalization-indicator"
          >
            <Sparkles className="h-4 w-4 text-primary" />
          </Button>
        ) : (
          <Badge
            variant="secondary"
            className={`cursor-pointer gap-1.5 hover-elevate ${className}`}
            data-testid="badge-personalization-indicator"
          >
            <Sparkles className="h-3 w-3" />
            <span>Personalized</span>
            {activeCount > 1 && (
              <span className="text-muted-foreground">({activeCount})</span>
            )}
          </Badge>
        )}
      </HoverCardTrigger>
      <HoverCardContent 
        className="w-80" 
        align="end"
        data-testid="popover-personalization-details"
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h4 className="text-sm font-semibold">Active Personalization</h4>
          </div>
          
          <p className="text-xs text-muted-foreground">
            AI responses are being customized based on the following context:
          </p>

          <div className="space-y-2">
            {details.hasVoiceProfile && (
              <div className="space-y-1" data-testid="section-voice-profile">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <User className="h-3.5 w-3.5" />
                  <span>User Profile</span>
                </div>
                <div className="flex flex-wrap gap-1 pl-5">
                  {details.voiceProfileFields.slice(0, 4).map((field) => (
                    <Badge 
                      key={field} 
                      variant="outline" 
                      className="text-xs py-0"
                    >
                      {FIELD_LABELS[field] || field}
                    </Badge>
                  ))}
                  {details.voiceProfileFields.length > 4 && (
                    <Badge variant="outline" className="text-xs py-0">
                      +{details.voiceProfileFields.length - 4} more
                    </Badge>
                  )}
                </div>
              </div>
            )}

            {details.hasAgent && (
              <div className="space-y-1" data-testid="section-agent">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Bot className="h-3.5 w-3.5" />
                  <span>AI Agent</span>
                </div>
                <div className="pl-5 text-xs text-muted-foreground">
                  <span className="font-medium">{details.agentName}</span>
                  {details.agentLens && (
                    <span> with {details.agentLens} lens</span>
                  )}
                </div>
              </div>
            )}

            {details.sourceCount > 0 && (
              <div className="space-y-1" data-testid="section-sources">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <FileText className="h-3.5 w-3.5" />
                  <span>Reference Sources ({details.sourceCount})</span>
                </div>
                <div className="pl-5 text-xs text-muted-foreground">
                  {details.sourceNames.slice(0, 3).map((name, index) => (
                    <div key={index} className="truncate">{name}</div>
                  ))}
                  {details.sourceNames.length > 3 && (
                    <div className="text-muted-foreground/70">
                      +{details.sourceNames.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            )}

            {details.spaceCount > 0 && (
              <div className="space-y-1" data-testid="section-spaces">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Layers className="h-3.5 w-3.5" />
                  <span>Chain of thoughts ({details.spaceCount})</span>
                </div>
                <div className="pl-5 text-xs text-muted-foreground">
                  {details.spaceNames.join(", ")}
                  {details.hasSpaceLens && (
                    <span className="ml-1 text-primary">(with lens)</span>
                  )}
                </div>
              </div>
            )}

            {details.hasTopicContext && (
              <div className="space-y-1" data-testid="section-topic">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <FolderOpen className="h-3.5 w-3.5" />
                  <span>Topic Focus</span>
                </div>
                <div className="pl-5 text-xs text-muted-foreground">
                  {details.topicName}
                </div>
              </div>
            )}

            {(details.sourceOfTruthCount ?? 0) > 0 && (
              <div className="space-y-1" data-testid="section-source-of-truth">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Shield className="h-3.5 w-3.5" />
                  <span>Source of Truth ({details.sourceOfTruthCount})</span>
                </div>
                <div className="pl-5 text-xs text-muted-foreground">
                  Global canon documents always in context
                </div>
              </div>
            )}

            {details.chatMode !== "normal" && (
              <div className="space-y-1" data-testid="section-chat-mode">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <MessageSquare className="h-3.5 w-3.5" />
                  <span>Chat Mode</span>
                </div>
                <div className="pl-5 text-xs text-muted-foreground">
                  {CHAT_MODE_LABELS[details.chatMode] || details.chatMode} mode active
                </div>
              </div>
            )}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

export function PersonalizationIndicatorCompact({ 
  isPersonalized, 
  onClick,
  className = "" 
}: { 
  isPersonalized: boolean; 
  onClick?: () => void;
  className?: string;
}) {
  if (!isPersonalized) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors ${className}`}
      data-testid="button-personalization-compact"
    >
      <Sparkles className="h-3 w-3" />
      <span>Personalized</span>
    </button>
  );
}
